/**
 * ChatWelcomeView - 欢迎页面视图组件
 * 从 ChatPage.tsx 提取的欢迎/新对话页面逻辑
 */

import { Message } from '@/types';
import ChatInput from '@/components/ChatInput';
import AgentIcon from '@/components/AgentIcon';
import { useTranslation } from 'react-i18next';
import { branding } from '@/config/branding';

export interface ChatWelcomeViewProps {
  selectedAgent?: {
    id: string;
    name: string;
    logo?: string;
    description?: string;
    config?: any;
  } | null;
  isReadOnlyMode: boolean;
  isSwitchingConversation: boolean;
  currentIsLoading: boolean;
  actualProjectId?: string | null;
  actualProjectName?: string | null;
  currentConversationId?: string;
  currentConversationAgent?: any;
  isCurrentGroupChat: boolean;
  replyToMessage?: Message | null;

  // Callbacks
  handleSendMessage: (
    content: string,
    isRetry?: boolean,
    options?: Record<string, any>
  ) => Promise<void>;
  handleStopRequest: () => void;
  handleNewConversation: () => void;
  handleStopGroupChat?: () => void;
  onProjectClick?: (projectId: string) => void;
  setReplyToMessage: (message: Message | null) => void;

  // 🔥 任务错误相关
  isWaitingForMessageInit?: boolean;
  currentTaskError?: string | null;
  onClearTaskError?: () => void;
}

export function ChatWelcomeView({
  selectedAgent,
  isReadOnlyMode,
  isSwitchingConversation,
  currentIsLoading,
  actualProjectId,
  actualProjectName,
  currentConversationId,
  currentConversationAgent,
  isCurrentGroupChat,
  replyToMessage,
  handleSendMessage,
  handleStopRequest,
  handleNewConversation,
  handleStopGroupChat,
  onProjectClick,
  setReplyToMessage,
  isWaitingForMessageInit = false,
  currentTaskError = null,
  onClearTaskError,
}: ChatWelcomeViewProps) {
  const { t } = useTranslation();

  return (
    <div className="flex-1 flex flex-col relative">
      <div className="flex-1 flex flex-col justify-center items-center p-6">
        <div className="text-center max-w-2xl mx-auto mb-8">
          {selectedAgent ? (
            // Show selected agent info
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center">
                <AgentIcon
                  agent={selectedAgent}
                  className="w-8 h-8 rounded-lg object-cover mr-1"
                />
                <h1 className="text-4xl font-normal text-gray-700 mb-2">
                  {selectedAgent.name}
                </h1>
              </div>
              {selectedAgent.description && (
                <p className="text-gray-500 text-base mt-2">
                  {selectedAgent.description}
                </p>
              )}
            </div>
          ) : (
            // Default welcome message
            <h1 className="text-4xl font-normal text-gray-700 mb-4">
              {t('chat.welcomeMessage') || 'Hello! How can I help you today?'}
            </h1>
          )}
        </div>

        {/* Chat Input in center - disabled if sharing */}
        {!isReadOnlyMode ? (
          <div className="w-full">
            <div className="max-w-3xl mx-auto rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
              <ChatInput
                onSendMessage={handleSendMessage}
                onStopRequest={handleStopRequest}
                onNewConversation={handleNewConversation}
                disabled={
                  isSwitchingConversation ||
                  isWaitingForMessageInit ||
                  !!currentTaskError
                }
                isLoading={currentIsLoading}
                taskError={currentTaskError}
                onClearTaskError={onClearTaskError}
                centered={true}
                projectId={actualProjectId}
                projectName={actualProjectName}
                placeholder={
                  t('chat.replyPlaceholder') || `Message ${branding.shortName}`
                }
                agentConfig={selectedAgent?.config}
                conversationId={currentConversationId}
                conversationAgent={currentConversationAgent}
                onProjectClick={onProjectClick}
                isGroupChat={isCurrentGroupChat}
                replyToMessage={replyToMessage}
                onClearReply={() => setReplyToMessage(null)}
                onStopGroupChat={
                  isCurrentGroupChat ? handleStopGroupChat : undefined
                }
              />
            </div>
          </div>
        ) : (
          // Show sharing/read-only message
          <div className="w-full max-w-5xl mb-8 text-center">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <p className="text-blue-800 text-lg mb-2">
                {t('chat.readOnly') || 'Read-only'}
              </p>
              <p className="text-blue-600 text-sm">
                {t('chat.readOnlyDescription') ||
                  'You are viewing a shared conversation. Chatting is disabled in this mode.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatWelcomeView;
